export type ThemeStyle = "modern" | "corporate" | "synthwave";
export type ThemeMode = "light" | "dark";

const baseTheme = {
  spacing: {
    xs: "0.25rem",
    sm: "0.5rem",
    md: "1rem",
    lg: "1.5rem",
    xl: "2rem",
  },
  typography: {
    sizes: {
      xs: "0.75rem",
      sm: "0.875rem",
      md: "1rem",
      lg: "1.25rem",
      xl: "1.5rem",
      xxl: "2rem",
    },
  },
  breakpoints: {
    sm: "576px",
    md: "768px",
    lg: "992px",
    xl: "1200px",
  },
};

const borderRadiusStyles = {
  modern: { sm: "0.25rem", md: "0.5rem", lg: "1rem", xl: "1.5rem" },
  corporate: { sm: "0rem", md: "0.125rem", lg: "0.25rem", xl: "0.5rem" },
  synthwave: { sm: "0.5rem", md: "1rem", lg: "2rem", xl: "3rem" },
};

const colorPalettes = {
  modern: {
    light: {
      primary: "#0070f3",
      accent: "#7c3aed",
      gradient: "linear-gradient(135deg, #0070f3 0%, #7c3aed 100%)",
      elevated: "#fbfbfc",
      primaryForeground: "#ffffff",
      background: "#f4f4f5",
      surface: "#ffffff",
      text: "#333333",
      textMuted: "#888888",
      border: "#eaeaea",
      success: "#0070f3",
      danger: "#e00",
      warning: "#f5a623",
      info: "#3b82f6",
    },
    dark: {
      primary: "#3291ff",
      accent: "#a855f7",
      gradient: "linear-gradient(135deg, #3291ff 0%, #a855f7 100%)",
      elevated: "#121212",
      primaryForeground: "#ffffff",
      background: "#0a0a0a",
      surface: "#171717",
      text: "#ffffff",
      textMuted: "#a1a1aa",
      border: "#27272a",
      success: "#3291ff",
      danger: "#ff1a1a",
      warning: "#f5a623",
      info: "#3b82f6",
    },
  },
  corporate: {
    light: {
      primary: "#000000",
      accent: "#4b5563",
      gradient: "linear-gradient(135deg, #111111 0%, #4b5563 100%)",
      elevated: "#fcfcfd",
      primaryForeground: "#ffffff",
      background: "#f8f9fa",
      surface: "#ffffff",
      text: "#111111",
      textMuted: "#666666",
      border: "#e5e5e5",
      success: "#059669",
      danger: "#e11d48",
      warning: "#d97706",
      info: "#2563eb",
    },
    dark: {
      primary: "#ffffff",
      accent: "#9ca3af",
      gradient: "linear-gradient(135deg, #ffffff 0%, #9ca3af 100%)",
      elevated: "#0a0a0a",
      primaryForeground: "#000000",
      background: "#000000",
      surface: "#111111",
      text: "#f3f4f6",
      textMuted: "#737373",
      border: "#222222",
      success: "#10b981",
      danger: "#f43f5e",
      warning: "#fbbf24",
      info: "#60a5fa",
    },
  },
  synthwave: {
    light: {
      primary: "#ff00a0",
      primaryForeground: "#ffffff",
      background: "#fde0ff",
      accent: "#00e5ff",
      gradient: "linear-gradient(135deg, #ff00a0 0%, #00e5ff 100%)",
      elevated: "#fff0ff",
      surface: "#ffffff",
      text: "#3b003c",
      textMuted: "#8b3a8c",
      border: "#d48cd5",
      success: "#00f0ff",
      danger: "#ff0055",
      warning: "#ffb800",
      info: "#00e5ff",
    },
    dark: {
      primary: "#ff00a0",
      primaryForeground: "#ffffff",
      background: "#0c0414",
      accent: "#00f0ff",
      gradient: "linear-gradient(135deg, #ff00a0 0%, #00f0ff 100%)",
      elevated: "#150726",
      surface: "#1a082e",
      text: "#ffcbf9",
      textMuted: "#a560c5",
      border: "#43137a",
      success: "#00f0ff",
      danger: "#ff0055",
      warning: "#ffb800",
      info: "#00e5ff",
    },
  },
};

export const getTheme = (style: ThemeStyle, mode: ThemeMode) => {
  return {
    ...baseTheme,
    style,
    mode,
    borderRadius: borderRadiusStyles[style],
    colors: colorPalettes[style][mode],
  };
};
